import { Button, Link } from "@mui/material";
import clsx from "clsx";
import { FC, useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSetRecoilState } from "recoil";

import { TextInputByControl } from "@ryde/controls";
import { useMfaLogin } from "@ryde/redux";
import {
  AUTH_MFA_RESEND_COOLDOWN_SECONDS,
  IAuthMfaForm,
  loginFlowStepAtom,
  LoginFormFlow,
  mfaChallengeTokenAtom,
  MILLISECONDS_IN_SECOND,
  OTP_LENGTH,
} from "@ryde/shared";

import { LoginPageFormContext } from "../login.page.form.context";
import { textboxLoginSx } from "../login.page.styled";
import { useLoginTranslation } from "../use-login-translation";
import { getButtonLoginSx } from "./login.parts.styled";

export const LoginMfaForm: FC = () => {
  const { control, getValues, watch } = useFormContext<IAuthMfaForm>();
  const { t } = useLoginTranslation();
  const setLoginFlowStep = useSetRecoilState(loginFlowStepAtom);
  const setMfaChallengeToken = useSetRecoilState(mfaChallengeTokenAtom);
  const { mfaVerify, mfaResend } = useMfaLogin();
  const { isNonProductionLoginColors } = useContext(LoginPageFormContext);

  const [resendSecondsLeft, setResendSecondsLeft] = useState(
    AUTH_MFA_RESEND_COOLDOWN_SECONDS,
  );

  const codeValue = watch("code") as string | undefined;
  const isVerifyDisabled = !codeValue?.length;
  const isResendDisabled = resendSecondsLeft > 0;

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return;
    }

    const timerId = window.setInterval(() => {
      setResendSecondsLeft(s => (s > 0 ? s - 1 : 0));
    }, MILLISECONDS_IN_SECOND);

    return () => window.clearInterval(timerId);
  }, [resendSecondsLeft]);

  const onVerifyClick = (): void => {
    const code = getValues("code");
    mfaVerify(code);
  };

  const onResendClick = async (): Promise<void> => {
    const sent = await mfaResend();
    if (sent) {
      setResendSecondsLeft(AUTH_MFA_RESEND_COOLDOWN_SECONDS);
    }
  };

  const onBackClick = (): void => {
    setMfaChallengeToken(undefined);
    setLoginFlowStep(LoginFormFlow.GENERIC_STEP);
  };

  return (
    <div className="LoginGenericForm">
      <div className="LoginGenericForm-FieldContainer">
        <div className="LoginGenericForm-LabelSignIn">
          {t("login.MFA_ENTER_CODE")}
        </div>
        <TextInputByControl
          sx={textboxLoginSx}
          name="code"
          e2eData={"mfa_code_login_input"}
          control={control}
          inputProps={{ maxLength: OTP_LENGTH }}
          size="small"
        />
      </div>
      <Button
        data-e2e="button-login-mfa-verify"
        variant="contained"
        color={isNonProductionLoginColors ? "inherit" : "primary"}
        sx={getButtonLoginSx(isNonProductionLoginColors)}
        onClick={onVerifyClick}
        disabled={isVerifyDisabled}
      >
        {t("login.MFA_VERIFY_BUTTON")}
      </Button>
      <Link
        data-e2e="link-login-mfa-resend"
        className={clsx("LoginGenericForm-ResendLink", {
          disabled: isResendDisabled,
        })}
        onClick={isResendDisabled ? undefined : onResendClick}
      >
        {t("login.MFA_RESEND_CODE")}
        {isResendDisabled ? ` (${resendSecondsLeft}s)` : ""}
      </Link>
      <Link style={{ cursor: "pointer" }} onClick={onBackClick}>
        ← {t("login.BACK")}
      </Link>
    </div>
  );
};
